import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { AssetContext } from "../../../AssetContextWrapper";
import { AssetRealEstate } from "../../../models/AssetDetail";
import { estateApi } from "../../../apis";
import { SingleSelectOptionForm } from "../shared/SingleSelectOptionForm";
import { Typography } from "@mui/material";

export enum Outcomes {
  heldInTrust = "Held in Trust",
  soleOwnershipFeeSimple = "Individually - Sole ownership/Fee Simple",
  tenancyByTheEntirety = "Tenancy by the Entirety (TBE)",
  communityProperty = "Community Property",
  tenantsInCommon = "Tenants in Common (TIC)",
  jointTenants = "Joint Tenants/Joint Tenancy",
  jointWithRightOfSurvivorship = "Joint with right of survivorship (JWROS)",
  notSure = "I don’t know",
  titleType = "Title type",
  skip = "Skip",
}

// const exactTitlingQuestion = "What is the exact titling, if you know it?";

export const PropertyTitleTypeScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { assetsLoaded } = useContext(AssetContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const primaryRealEstate = activeEstate?.primaryRealEstate;
  const reAsset = primaryRealEstate?.detail as AssetRealEstate;

  useEffect(() => {
    if (assetsLoaded && !activeEstate?.primaryRealEstate) {
      currentScreen &&
        flow?.navigateToNext(currentScreen, Outcomes.skip, navigate, {
          replace: true,
        });
    }
  }, [assetsLoaded]);

  const handleComplete = async (titleType: string): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (activeEstate?.id && user?.id && activeEstate?.primaryRealEstate?.detail) {
      (activeEstate.primaryRealEstate.detail as AssetRealEstate).title_type =
        titleType;
      try {
        await estateApi.saveAsset(primaryRealEstate,activeEstate.id, user.id);
      } catch (error) {
        throw new Error("could not save data");
      }
    }

    const outcome =
      titleType === Outcomes.notSure ? Outcomes.notSure : Outcomes.titleType;

    flow?.navigateToNext(currentScreen, outcome, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper
      title="Tell us about how the home is titled"
      subTitle="This information can be found through the physical or digital Property Tax Records of the county the home
      is located in. We’ll need it to know how exactly the home gets transferred or inherited during the Probate process."
    >
      <SingleSelectOptionForm
        initAnswer={reAsset?.title_type}
        answerLabel="Home Title Type"
        // errorText={`We need to know whether or not ${activeEstate?.deceasedFirstName} owned this property.`}
        options={[
          { value: Outcomes.heldInTrust },
          {
            value: Outcomes.soleOwnershipFeeSimple,
          },
          {
            value: Outcomes.tenancyByTheEntirety,
          },
          { value: Outcomes.communityProperty },
          { value: Outcomes.tenantsInCommon },
          { value: Outcomes.jointTenants },
          {
            value: Outcomes.jointWithRightOfSurvivorship,
          },
          { value: Outcomes.notSure },
        ]}
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
        helpContent={
          <Typography
            variant="caption"
            sx={{
              ml: 2,
            }}
          >
            Learn more about the{" "}
            <a
              href="https://www.weareatticus.com/learn/real-estate-ownership"
              target="_blank"
            >
              different types of ownership
            </a>
          </Typography>
        }
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
